<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type { DropdownItem } from "@/types/ui";

import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";

const props = defineProps({
  listData: {
    type: Array<DropdownItem>,
    default: [],
  },
  icon: {
    type: String,
    default: 'fa-light fa-bars',
  },
  label: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  onRight: {
    type: Boolean,
    default: false,
  },
});

const { i18next } = useTranslation();
const { sleep } = useTiming();
useComponentErrorCapture();
const emit = defineEmits(["itemClick", "show"]);

const show = ref(false);
const onBottom = ref(false);
const menuMain = ref();

watch(show, async (val) => {
  // For animation
  if (val == false) {
    await sleep(200)
  }
  
  emit("show", val);
})

const growMenuMain = computed(() => {
  if (props.onRight) {
    return 'grow'
  }
  return 'grow-from-left';
})

function itemClick(item: DropdownItem) {
  emit("itemClick", item);
  show.value = false;
}

function outsideClick() {
  show.value = false;
}

async function showDropdown() {
  const bodyEl = document.getElementsByTagName('body')[0];
  if (bodyEl) {
    bodyEl.click();
  }
  onBottom.value = false;
  show.value = true;
  checkPosition(menuMain.value);
}

async function checkPosition(el: HTMLElement) {
  await sleep(500);
  const rect = el.getBoundingClientRect();
  const isOutOfView = rect.bottom > window.innerHeight - 50;
  onBottom.value = isOutOfView;
}

</script>
<template>
  <div
    class="dropdown-menu"
    :class="{'dropdown-menu--on-right': onRight, 'dropdown-menu--on-bottom': onBottom, 'dropdown-menu--open': show}">
    <div class="dropdown-menu__trigger">
      <template v-if="!!$slots.trigger">
        <div @click.stop="showDropdown">
          <slot name="trigger" />
        </div>
      </template>
      <button 
        v-else
        @click.stop="showDropdown"
        class="button button--secondary button--icon">
        <i :class="icon"></i>
      </button>
    </div>
    <Transition name="opacity-fast">
      <div
        @click.stop="show = false"
        v-show="show"
        class="dropdown-menu__background">
      </div>
    </Transition>
    <Transition :name="growMenuMain">
      <div ref="menuMain" v-show="show" class="dropdown-menu__main" :class="{'dropdown-menu__main--fixed': title && title.length > 0}">
        <span v-if="title && title.length > 0"class="dropdown-menu__main__title">{{ title }}</span>
        <ul class="dropdown-menu__list" v-click-outside="outsideClick">
          <li
            v-for="item in listData"
            @click.stop="itemClick(item)"
            class="dropdown-menu__list__item"
            :class="{'dropdown-menu__list__item--disabled': item.disabled}">
            <div class="dropdown-menu__list__item__main">
              <i v-if="item.icon" class="dropdown-menu__list__item__icon" :class="item.icon"></i>
              <span v-if="item.label" class="dropdown-menu__list__item__label">
                <template v-if="i18next.exists(`${item.label}`)">{{ $t(`${item.label}`) }}</template>
                <template v-else>{{ item.label }}</template>
              </span>
            </div>
            <p v-if="item.description" class="dropdown-menu__list__item__description">
              {{ item.description }}
            </p>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>